import { useEffect, useState, useCallback, useRef } from 'react';
import { Node } from 'reactflow';
import { estimateCost, CostEstimationResult, ResourceCostRequest } from '../api/cost';

interface CostState extends Partial<CostEstimationResult> {
  loading: boolean;
  error: string | null;
  isRefreshing?: boolean; // Refresh silencieux en arrière-plan
}

/**
 * Hook pour l'estimation de coût en temps réel
 *
 * Calcule automatiquement le coût total d'une architecture basée sur les nodes ReactFlow
 * avec debounce pour éviter trop de requêtes API
 *
 * @param nodes - Liste des nodes ReactFlow
 * @param currency - Devise (USD par défaut)
 * @param debounceMs - Délai de debounce en ms (2000 par défaut pour éviter le tremblage)
 * @param architectureId - ID de l'architecture (optionnel, pour résolution des variables)
 */
export function useCostEstimation(
  nodes: Node[],
  currency: string = 'USD',
  debounceMs: number = 2000,
  architectureId?: string
) {
  const [cost, setCost] = useState<CostState>({
    totalMonthly: 0,
    totalHourly: 0,
    currency: 'USD',
    breakdown: [],
    unsupported: [],
    loading: false,
    error: null,
    ok: true,
    isRefreshing: false
  });

  // Ref pour garder trace si c'est le premier chargement
  const isFirstLoad = useRef(true);

  const estimateArchitecture = useCallback(async () => {
    // Filtrer les nodes qui ne sont pas des annotations
    const resourceNodes = nodes.filter(
      (n) => n.type !== 'annotation' && n.data?.tfKey
    );

    if (resourceNodes.length === 0) {
      setCost({
        totalMonthly: 0,
        totalHourly: 0,
        currency,
        breakdown: [],
        unsupported: [],
        loading: false,
        error: null,
        ok: true,
        isRefreshing: false
      });
      isFirstLoad.current = false;
      return;
    }

    // Premier chargement : afficher le loading
    // Sinon : refresh silencieux en arrière-plan (isRefreshing)
    if (isFirstLoad.current) {
      setCost((prev) => ({ ...prev, loading: true, error: null }));
    } else {
      setCost((prev) => ({ ...prev, isRefreshing: true, error: null }));
    }

    try {
      // Convertir les nodes ReactFlow en format attendu par l'API
      const resources: ResourceCostRequest[] = resourceNodes.map((node) => ({
        id: node.id,
        type: node.data.tfKey || node.data.resourceType || '',
        params: {
          ...node.data.config,
          ...node.data.params,
          name: node.data.label || node.data.name,
          label: node.data.label
        }
      }));

      const result = await estimateCost(resources, currency, architectureId);

      // Mise à jour avec transition douce
      setCost({
        ...result,
        loading: false,
        error: null,
        isRefreshing: false
      });

      isFirstLoad.current = false;
    } catch (error: any) {
      console.error('Cost estimation failed:', error);

      // Si c'est une erreur réseau, ne pas afficher d'erreur (système hybride avec cache)
      const isNetworkError =
        !error.response ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('Network error') ||
        error.message?.includes('network');

      if (isNetworkError) {
        console.warn('Network error detected, using cached pricing data (hybrid system)');
        // Ne pas afficher d'erreur, le système hybride backend gère le fallback
        setCost((prev) => ({
          ...prev,
          loading: false,
          isRefreshing: false,
          error: null // Pas d'erreur visible pour l'utilisateur
        }));
      } else {
        // Pour les autres erreurs (validation, erreurs métier, etc.), afficher l'erreur
        setCost((prev) => ({
          ...prev,
          loading: false,
          isRefreshing: false,
          error: error.response?.data?.message || error.message || 'Failed to estimate cost'
        }));
      }
    }
  }, [nodes, currency, architectureId]);

  useEffect(() => {
    // Debounce pour éviter trop de requêtes
    const timeoutId = setTimeout(() => {
      estimateArchitecture();
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [estimateArchitecture, debounceMs]);

  // Fonction pour rafraîchir manuellement
  const refresh = useCallback(() => {
    estimateArchitecture();
  }, [estimateArchitecture]);

  return {
    ...cost,
    refresh
  };
}

/**
 * Formate un coût en devise
 */
export function formatCost(amount: number, currency: string = 'USD'): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£'
  };

  const symbol = symbols[currency] || currency;
  return `${symbol}${amount.toFixed(2)}`;
}

/**
 * Formate un coût avec la devise à la fin
 */
export function formatCostWithCurrency(amount: number, currency: string = 'USD'): string {
  return `${amount.toFixed(2)} ${currency}`;
}
